import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import axios from 'axios';
import {
  Building2, Users, TrendingUp, DollarSign, Activity,
  BarChart3, Shield, Ban, CheckCircle2, Loader2,
  MessageSquare, Calendar, UserPlus, Clock, Eye,
  AlertTriangle, FileText, RefreshCw, Crown,
  Zap, PieChart, Gauge, Smartphone, ArrowUpRight, ArrowDownRight, Bell,
  ShieldCheck, XCircle, ClipboardList, Film, ChevronRight, Tag, Gift, Percent, Copy, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DeveloperDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const urlTab = new URLSearchParams(routerLocation.search).get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || 'overview');
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [topUsers, setTopUsers] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [behaviorPatterns, setBehaviorPatterns] = useState(null);
  const [verifications, setVerifications] = useState(null);
  const [concessions, setConcessions] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [healthScores, setHealthScores] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const tabFromUrl = new URLSearchParams(routerLocation.search).get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    } else if (!tabFromUrl && activeTab !== 'overview') {
      setActiveTab('overview');
    }
  }, [routerLocation.search]);

  const fetchAll = async () => {
    setLoading(true);
    const [sRes, cRes, tRes, aRes, uRes, bRes, vRes, conRes, proRes, revRes, healthRes] = await Promise.all([
      axios.get(`${API_URL}/api/developer/stats`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/companies`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/developer/top-users`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/audit-logs`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/developer/usage-analytics`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/behavior-patterns`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/verifications`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/concessions`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/developer/promo-codes`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/developer/revenue`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/api/developer/onboarding-health`, { withCredentials: true }).catch(() => ({ data: null })),
    ]);
    setStats(sRes.data);
    setCompanies(cRes.data);
    setTopUsers(tRes.data);
    setAuditLogs(aRes.data);
    setUsageAnalytics(uRes.data);
    setBehaviorPatterns(bRes.data);
    setVerifications(vRes.data);
    setConcessions(conRes.data || []);
    setPromoCodes(proRes.data || []);
    setRevenueData(revRes.data);
    setHealthScores(healthRes.data);
    setLoading(false);
  };

  const suspendCompany = async (orgId) => {
    try {
      await axios.put(`${API_URL}/api/developer/companies/${orgId}/suspend`, {}, { withCredentials: true });
      toast.success('Company suspended - notification emails sent');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const restoreCompany = async (orgId) => {
    try {
      await axios.put(`${API_URL}/api/developer/companies/${orgId}/restore`, {}, { withCredentials: true });
      toast.success('Company restored - notification emails sent');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');

  const deleteCompany = async (orgId) => {
    try {
      const { data } = await axios.delete(`${API_URL}/api/developer/companies/${orgId}`, { withCredentials: true });
      toast.success(data.message);
      setDeleteConfirm(null);
      setDeleteInput('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const resendCeoInvite = async (orgId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/developer/companies/${orgId}/resend-ceo-invite`, {}, { withCredentials: true });
      if (data.email_sent) {
        toast.success('CEO invitation resent successfully');
      } else {
        toast.warning('CEO invitation created but email failed', {
          description: data.full_link ? `Share this link: ${data.full_link}` : 'Check the invitation link in the response.',
          duration: 12000,
          action: data.full_link ? {
            label: 'Copy Link',
            onClick: () => { navigator.clipboard.writeText(data.full_link); toast.success('Link copied'); }
          } : undefined
        });
      }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend invite'); }
  };

  const [bulkResending, setBulkResending] = useState(false);
  const [timelineData, setTimelineData] = useState({});
  const [expandedTimeline, setExpandedTimeline] = useState(null);

  const bulkResendCeoInvites = async () => {
    setBulkResending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/developer/bulk-resend-ceo-invites`, {}, { withCredentials: true });
      if (data.results?.length === 0) {
        toast.info('All CEOs are already registered — nothing to resend');
      } else {
        const sent = data.results.filter(r => r.email_sent).length;
        const failed = data.results.length - sent;
        toast.success(`Resent ${data.results.length} CEO invitation(s)`, {
          description: `${sent} delivered, ${failed} need manual link sharing`,
          duration: 10000
        });
      }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk resend failed'); }
    setBulkResending(false);
  };

  const fetchTimeline = async (orgId) => {
    if (expandedTimeline === orgId) { setExpandedTimeline(null); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/developer/companies/${orgId}/timeline`, { withCredentials: true });
      setTimelineData(prev => ({ ...prev, [orgId]: data.milestones }));
      setExpandedTimeline(orgId);
    } catch { toast.error('Failed to load timeline'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'health', label: 'Onboarding Health', icon: Activity },
    { id: 'usage', label: 'Usage Analytics', icon: PieChart },
    { id: 'behavior', label: 'Behavior & Churn', icon: Gauge },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'users', label: 'Top Users', icon: Users },
    { id: 'mobile', label: 'Mobile API', icon: Smartphone },
    { id: 'audit', label: 'Audit Log', icon: FileText },
    { id: 'verifications', label: 'Verifications', icon: ShieldCheck },
    { id: 'promotions', label: 'Promotions', icon: Zap },
    { id: 'marketing', label: 'Marketing', icon: Film },
    { id: 'access-analytics', label: 'Access Analytics', icon: Shield },
    { id: 'billing', label: 'Billing', icon: DollarSign },
  ];

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="developer-dashboard">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
            <Shield className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Platform Administration
          </h1>
          <p className="text-muted-foreground mt-1">Superadmin portal - Platform analytics & management</p>
        </div>

        <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={`whitespace-nowrap ${activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}`}
              onClick={() => setActiveTab(t.id)} data-testid={`dev-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Users', value: stats.total_users, icon: Users, color: '#00A6A0' },
                { label: 'Organizations', value: stats.total_organizations, icon: Building2, color: '#FF6F61' },
                { label: 'Total Revenue', value: `$${stats.total_revenue?.toFixed(2)}`, icon: DollarSign, color: '#A8E6CF' },
                { label: 'Active (7d)', value: stats.active_users_7d, icon: Activity, color: '#4A90E2' },
              ].map((s, i) => (
                <Card key={i} className="glass-card border-border">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-muted-foreground text-xs">{s.label}</p>
                        <p className="text-2xl font-heading font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
                      </div>
                      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${s.color}15` }}>
                        <s.icon className="w-5 h-5" style={{ color: s.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Active Orgs', value: stats.active_organizations, sub: `${stats.suspended_organizations} suspended` },
                { label: 'New Signups (30d)', value: stats.recent_signups_30d, sub: 'last 30 days' },
                { label: 'Total Shifts', value: stats.total_shifts, sub: 'all time' },
                { label: 'Total Messages', value: stats.total_messages, sub: `${stats.total_invitations} invitations` },
              ].map((s, i) => (
                <Card key={`secondary-${i}`} className="glass-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                    <p className="text-xl font-bold text-foreground mt-0.5">{s.value}</p>
                    <p className="text-[10px] text-muted-foreground">{s.sub}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Users by Role */}
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#00A6A0]" />Users by Role
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(stats.users_by_role || {}).map(([role, count]) => (
                    <div key={role} className="p-3 bg-muted rounded-lg border border-border text-center">
                      <p className="text-lg font-bold text-[#00A6A0]">{count}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{role?.replace('_', ' ')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {topUsers?.orgs_by_tier && (
              <Card className="glass-card border-border">
                <CardHeader>
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#FF6F61]" />Organizations by Subscription Tier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(topUsers.orgs_by_tier).map(([tier, count]) => (
                      <div key={tier} className="p-3 bg-muted rounded-lg border border-border text-center">
                        <p className="text-lg font-bold text-[#FF6F61]">{count}</p>
                        <p className="text-[10px] text-muted-foreground capitalize">{tier || 'No tier'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* REVENUE TAB */}
        {activeTab === 'revenue' && (
          <div className="space-y-6" data-testid="revenue-tab">
            {revenueData ? (
              <>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Total Revenue (USD)</p>
                      <p className="text-2xl font-heading font-bold text-[#A8E6CF] mt-1">${revenueData.total_revenue_usd.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Total Transactions</p>
                      <p className="text-2xl font-heading font-bold text-[#00A6A0] mt-1">{revenueData.total_transactions}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Currencies Used</p>
                      <p className="text-2xl font-heading font-bold text-[#FF6F61] mt-1">{revenueData.currency_breakdown.length || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Revenue Chart */}
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#A8E6CF]" />Monthly Revenue (USD)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {revenueData.monthly.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No revenue data yet. Revenue will appear here once payments are processed.</p>
                    ) : (
                      <div className="space-y-2">
                        {revenueData.monthly.map(m => (
                          <div key={m.month} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-20 font-mono">{m.month}</span>
                            <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-[#A8E6CF] rounded-full transition-all"
                                style={{ width: `${Math.min(100, (m.total_usd / Math.max(1, ...revenueData.monthly.map(x => x.total_usd))) * 100)}%` }} />
                            </div>
                            <span className="text-sm font-bold text-foreground w-24 text-right">${m.total_usd.toFixed(2)}</span>
                            <span className="text-[10px] text-muted-foreground w-12">{m.transactions} txn</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Currency Breakdown */}
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#FF6F61]" />Revenue by Currency
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {revenueData.currency_breakdown.length === 0 ? (
                      <p className="text-muted-foreground text-center py-6">No currency data yet.</p>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {revenueData.currency_breakdown.map(c => (
                          <div key={c.currency} className="p-4 bg-muted rounded-lg border border-border">
                            <p className="text-lg font-bold text-[#00A6A0]">{c.currency}</p>
                            <p className="text-sm text-foreground">{c.total_local.toLocaleString()} local</p>
                            <p className="text-xs text-muted-foreground">${c.total_usd.toFixed(2)} USD equiv.</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{c.transactions} transactions</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-10">Loading revenue data...</p>
            )}
          </div>
        )}

        {/* ONBOARDING HEALTH TAB */}
        {activeTab === 'health' && (
          <div className="space-y-6" data-testid="health-tab">
            {healthScores ? (
              <>
                <div className="grid sm:grid-cols-5 gap-3">
                  {['excellent', 'good', 'fair', 'at_risk', 'stalled'].map(h => {
                    const count = healthScores.scores.filter(s => s.health === h).length;
                    const colors = { excellent: '#10B981', good: '#A8E6CF', fair: '#F59E0B', at_risk: '#FF6F61', stalled: '#EF4444' };
                    return (
                      <Card key={h} className="glass-card border-border">
                        <CardContent className="p-4 text-center">
                          <p className="text-2xl font-bold" style={{ color: colors[h] }}>{count}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{h.replace('_', ' ')}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#00A6A0]" />Onboarding Health (milestones within 7 days of registration)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {healthScores.scores.map(s => {
                        const colors = { excellent: 'border-emerald-500/30 bg-emerald-500/5', good: 'border-[#A8E6CF]/30 bg-[#A8E6CF]/5', fair: 'border-amber-500/30 bg-amber-500/5', at_risk: 'border-[#FF6F61]/30 bg-[#FF6F61]/5', stalled: 'border-red-500/30 bg-red-500/5' };
                        const textColors = { excellent: 'text-emerald-400', good: 'text-[#A8E6CF]', fair: 'text-amber-400', at_risk: 'text-[#FF6F61]', stalled: 'text-red-400' };
                        return (
                          <div key={s.org_id} className={`p-4 rounded-lg border ${colors[s.health]}`} data-testid={`health-${s.org_id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-foreground font-medium">{s.name}</p>
                                <p className="text-[10px] text-muted-foreground font-mono">{s.cisc} — registered {s.registered}</p>
                              </div>
                              <div className="text-right">
                                <p className={`text-lg font-bold ${textColors[s.health]}`}>{s.score}/100</p>
                                <p className={`text-[10px] capitalize ${textColors[s.health]}`}>{s.health.replace('_', ' ')}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {['ceo_signup', 'first_caregiver', 'first_client', 'first_shift', 'first_payment'].map(m => (
                                <span key={m} className={`text-[9px] px-2 py-0.5 rounded-full ${
                                  s.milestones_hit.includes(m) ? 'bg-[#00A6A0]/20 text-[#00A6A0]' : 'bg-muted text-muted-foreground'
                                }`}>{m.replace('_', ' ').replace('first ', '')}</span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-10">Loading health scores...</p>
            )}
          </div>
        )}

        {/* USAGE ANALYTICS TAB */}
        {activeTab === 'usage' && (
          <div className="space-y-6" data-testid="usage-analytics-tab">
            {usageAnalytics ? (
              <>
                {/* KPI Row */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Retention Rate (7d)</p>
                      <p className="text-2xl font-bold text-[#00A6A0]">{usageAnalytics.retention_rate_7d}%</p>
                      <p className="text-[10px] text-muted-foreground">{usageAnalytics.active_users_7d} of {usageAnalytics.total_users} users</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Shift Completion Rate</p>
                      <p className="text-2xl font-bold text-[#FF6F61]">{usageAnalytics.shift_completion_rate}%</p>
                      <p className="text-[10px] text-muted-foreground">{usageAnalytics.completed_shifts} of {usageAnalytics.total_shifts}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Avg Messages/User</p>
                      <p className="text-2xl font-bold text-[#A8E6CF]">{usageAnalytics.avg_messages_per_user}</p>
                      <p className="text-[10px] text-muted-foreground">per active sender</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Avg Logins (30d)</p>
                      <p className="text-2xl font-bold text-[#4A90E2]">{usageAnalytics.login_stats?.avg_logins_30d || 0}</p>
                      <p className="text-[10px] text-muted-foreground">max: {usageAnalytics.login_stats?.max_logins_30d || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Feature Usage Heatmap */}
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#00A6A0]" />Feature Usage (30d)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(usageAnalytics.feature_usage || {}).length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No usage data yet. Activity tracking has just been enabled.</p>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(usageAnalytics.feature_usage)
                          .sort(([, a], [, b]) => b - a)
                          .map(([feature, count]) => {
                            const maxCount = Math.max(...Object.values(usageAnalytics.feature_usage), 1);
                            const pct = (count / maxCount) * 100;
                            return (
                              <div key={feature} className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground w-28 truncate capitalize">{feature.replace('_', ' ')}</span>
                                <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden border border-border">
                                  <div className="h-full bg-gradient-to-r from-[#00A6A0] to-[#008A85] rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                                </div>
                                <span className="text-xs font-bold text-[#00A6A0] w-12 text-right">{count}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Peak Hours Chart */}
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#FF6F61]" />Peak Usage Hours (24h)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-[2px] h-28">
                      {(usageAnalytics.peak_hours || []).map((h) => {
                        const maxH = Math.max(...(usageAnalytics.peak_hours || []).map(x => x.count), 1);
                        const height = (h.count / maxH) * 100;
                        return (
                          <div key={h.hour} className="flex-1 flex flex-col items-center gap-0.5" title={`${h.hour}:00 - ${h.count} actions`}>
                            <div className="w-full rounded-t" style={{ height: `${Math.max(height, 2)}%`, backgroundColor: h.count > maxH * 0.7 ? '#00A6A0' : h.count > maxH * 0.3 ? '#FF6F61' : 'hsl(var(--muted))' }} />
                            {h.hour % 4 === 0 && <span className="text-[7px] text-muted-foreground">{h.hour}h</span>}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Day of Week */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Card className="glass-card border-border">
                    <CardHeader>
                      <CardTitle className="text-base text-foreground flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[#A8E6CF]" />Activity by Day of Week
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {(usageAnalytics.day_of_week || []).map(d => {
                          const maxD = Math.max(...(usageAnalytics.day_of_week || []).map(x => x.count), 1);
                          const pct = (d.count / maxD) * 100;
                          return (
                            <div key={d.day} className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-8">{d.day}</span>
                              <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden border border-border">
                                <div className="h-full bg-[#A8E6CF] rounded-full" style={{ width: `${Math.max(pct, 2)}%` }} />
                              </div>
                              <span className="text-xs text-[#A8E6CF] w-10 text-right">{d.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Daily Active Users */}
                  <Card className="glass-card border-border">
                    <CardHeader>
                      <CardTitle className="text-base text-foreground flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#4A90E2]" />Daily Active Users (14d)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(usageAnalytics.daily_active_users || []).length === 0 ? (
                        <p className="text-muted-foreground text-center py-4 text-sm">Collecting data...</p>
                      ) : (
                        <div className="flex items-end gap-1 h-28">
                          {(usageAnalytics.daily_active_users || []).map(d => {
                            const maxDAU = Math.max(...(usageAnalytics.daily_active_users || []).map(x => x.users), 1);
                            const height = (d.users / maxDAU) * 100;
                            return (
                              <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.users} users`}>
                                <span className="text-[8px] text-[#4A90E2]">{d.users}</span>
                                <div className="w-full bg-[#4A90E2] rounded-t" style={{ height: `${Math.max(height, 4)}%` }} />
                                <span className="text-[6px] text-muted-foreground">{d.date?.slice(8)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-10">Loading usage analytics...</p>
            )}
          </div>
        )}

        {/* BEHAVIOR & CHURN TAB */}
        {activeTab === 'behavior' && (
          <div className="space-y-6" data-testid="behavior-patterns-tab">
            {behaviorPatterns ? (
              <>
                {/* Churn Metrics */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Churn Rate (30d)</p>
                      <p className={`text-2xl font-bold ${behaviorPatterns.churn_rate_30d > 30 ? 'text-red-400' : behaviorPatterns.churn_rate_30d > 15 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {behaviorPatterns.churn_rate_30d}%
                      </p>
                      <p className="text-[10px] text-muted-foreground">{behaviorPatterns.churned_users} churned of {behaviorPatterns.previously_active} active</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Churned Users</p>
                      <p className="text-2xl font-bold text-red-400">{behaviorPatterns.churned_users}</p>
                      <p className="text-[10px] text-muted-foreground">active 60d ago, inactive 30d</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground">Organizations Tracked</p>
                      <p className="text-2xl font-bold text-[#00A6A0]">{behaviorPatterns.org_patterns?.length || 0}</p>
                      <p className="text-[10px] text-muted-foreground">with behavior data</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Organization Behavior Patterns */}
                <Card className="glass-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      <Gauge className="w-5 h-5 text-[#00A6A0]" />Organization Health & Churn Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(behaviorPatterns.org_patterns || []).length === 0 ? (
                      <p className="text-muted-foreground text-center py-6">No organization behavior data yet. Activity tracking is collecting data.</p>
                    ) : (
                      <div className="space-y-3">
                        {behaviorPatterns.org_patterns.map(org => (
                          <div key={org.org_id} className={`p-4 bg-muted rounded-lg border ${org.churn_risk === 'high' ? 'border-red-500/30' : org.churn_risk === 'medium' ? 'border-amber-500/30' : 'border-emerald-500/30'}`}
                            data-testid={`org-behavior-${org.org_id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div>
                                  <p className="text-sm text-foreground font-medium">{org.name}</p>
                                  <p className="text-[10px] text-muted-foreground font-mono">{org.cisc}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                  org.churn_risk === 'high' ? 'bg-red-500/10 text-red-400' :
                                  org.churn_risk === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                                  'bg-emerald-500/10 text-emerald-400'
                                }`}>
                                  {org.churn_risk === 'high' ? 'High Risk' : org.churn_risk === 'medium' ? 'Medium Risk' : 'Healthy'}
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${org.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                  {org.status || 'pending'}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#00A6A0]">{org.active_users_30d}/{org.total_users}</p>
                                <p className="text-[9px] text-muted-foreground">Active/Total Users</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#FF6F61]">{org.total_actions_30d}</p>
                                <p className="text-[9px] text-muted-foreground">Actions (30d)</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-[#A8E6CF]">{org.top_features?.map(f => f.feature).join(', ') || 'None'}</p>
                                <p className="text-[9px] text-muted-foreground">Top Features</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-10">Loading behavior patterns...</p>
            )}
          </div>
        )}

        {/* COMPANIES TAB */}
        {activeTab === 'companies' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>{companies.length} organizations</span>
                <Button size="sm" variant="ghost" className="text-[#00A6A0]" onClick={fetchAll} data-testid="refresh-companies-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
                </Button>
              </div>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={bulkResendCeoInvites} disabled={bulkResending}
                data-testid="bulk-resend-ceo-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${bulkResending ? 'animate-spin' : ''}`} />
                {bulkResending ? 'Resending...' : 'Resend All Failed CEO Invites'}
              </Button>
            </div>
            {companies.map(org => (
              <Card key={org.id} className={`glass-card border ${org.subscription_status === 'suspended' ? 'border-red-500/30' : 'border-border'}`}
                data-testid={`company-${org.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {org.logo_url ? (
                        <img src={org.logo_url} alt={org.name} className="w-10 h-10 rounded-lg object-cover border border-border" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-foreground font-medium">{org.name}</p>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span className="font-mono text-[#FF6F61]">{org.cisc}</span>
                          <span>{org.user_count} users</span>
                          <span className={`px-1.5 py-0.5 rounded-full ${org.subscription_status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : org.subscription_status === 'suspended' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                            {org.subscription_status || 'pending'}
                          </span>
                          {org.subscription_tier && (
                            <span className="px-1.5 py-0.5 rounded-full bg-muted">{org.subscription_tier}</span>
                          )}
                        </div>
                        {org.ceo_email && (
                          <div className="flex items-center gap-2 mt-1 text-[10px]">
                            <Crown className="w-3 h-3 text-amber-400" />
                            <span className="text-muted-foreground">{org.ceo_name || 'CEO'}: {org.ceo_email}</span>
                            {org.ceo_registered ? (
                              <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">Registered</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Not registered</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 items-end">
                      <div className="flex gap-2">
                        {org.subscription_status === 'suspended' ? (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            onClick={() => restoreCompany(org.id)} data-testid={`restore-${org.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Restore
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs"
                            onClick={() => suspendCompany(org.id)} data-testid={`suspend-${org.id}`}>
                            <Ban className="w-3.5 h-3.5 mr-1" />Suspend
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs"
                          onClick={() => setDeleteConfirm(org)} data-testid={`delete-${org.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                        </Button>
                      </div>
                      {org.ceo_email && !org.ceo_registered && (
                        <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 text-xs"
                          onClick={() => resendCeoInvite(org.id)} data-testid={`resend-ceo-${org.id}`}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" />Resend CEO Invite
                        </Button>
                      )}
                    </div>
                  </div>
                  {org.address && <p className="text-[10px] text-muted-foreground mt-2 ml-14">{org.address}</p>}
                  {/* Org controls: External Job Blocking + HR Populate */}
                  <div className="ml-14 mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className={`text-[10px] h-6 px-2 ${org.block_external_jobs_enabled ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}`}
                      onClick={async () => {
                        try {
                          const endpoint = org.block_external_jobs_enabled
                            ? `${API_URL}/api/org/${org.id}/disable-external-job-blocking`
                            : `${API_URL}/api/org/${org.id}/enable-external-job-blocking`;
                          await axios.put(endpoint, {}, { withCredentials: true });
                          toast.success(org.block_external_jobs_enabled ? 'External job blocking disabled' : 'External job blocking enabled');
                          fetchAll();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }} data-testid={`toggle-job-block-${org.id}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {org.block_external_jobs_enabled ? 'Job Block: ON' : 'Job Block: OFF'}
                    </Button>
                    <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 border-border text-muted-foreground"
                      onClick={async () => {
                        try {
                          await axios.post(`${API_URL}/api/admin/migrate-hr-compliance`, {}, { withCredentials: true });
                          toast.success('HR compliance migrated for all orgs');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }} data-testid={`migrate-hr-${org.id}`}>
                      <FileText className="w-3 h-3 mr-1" />Populate HR Docs
                    </Button>
                    <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 border-border text-muted-foreground"
                      onClick={async () => {
                        try {
                          await axios.post(`${API_URL}/api/subscriptions/auto-regulate`, {}, { withCredentials: true });
                          toast.success('Subscriptions auto-regulated');
                          fetchAll();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }} data-testid={`auto-regulate-${org.id}`}>
                      <Gauge className="w-3 h-3 mr-1" />Auto-Regulate
                    </Button>
                  </div>
                  {/* Timeline toggle */}
                  <div className="ml-14 mt-2">
                    <button onClick={() => fetchTimeline(org.id)}
                      className="text-[10px] text-[#00A6A0] hover:underline" data-testid={`timeline-toggle-${org.id}`}>
                      {expandedTimeline === org.id ? 'Hide timeline' : 'View onboarding timeline'}
                    </button>
                    {expandedTimeline === org.id && timelineData[org.id] && (
                      <div className="mt-2 pl-3 border-l-2 border-[#00A6A0]/30 space-y-2">
                        {timelineData[org.id].length === 0 ? (
                          <p className="text-[10px] text-muted-foreground">No milestones yet — onboarding stalled</p>
                        ) : timelineData[org.id].map((m, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-2 h-2 mt-1 rounded-full bg-[#00A6A0] flex-shrink-0" />
                            <div>
                              <p className="text-[11px] text-foreground">{m.event}</p>
                              <p className="text-[9px] text-muted-foreground">
                                {m.date ? new Date(m.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) { setDeleteConfirm(null); setDeleteInput(''); } }}>
              <DialogContent className="bg-background border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />Permanently Delete Company
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <p className="text-sm text-muted-foreground">
                    This will permanently delete <span className="text-foreground font-semibold">{deleteConfirm?.name}</span> and all associated data including users, shifts, messages, clinical logs, and documents. This cannot be undone.
                  </p>
                  <div>
                    <Label className="text-xs text-muted-foreground">Type DELETE to confirm</Label>
                    <Input value={deleteInput} onChange={(e) => setDeleteInput(e.target.value)}
                      placeholder="DELETE" className="mt-1" data-testid="delete-confirm-input" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }}>
                      Cancel
                    </Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                      disabled={deleteInput !== 'DELETE'}
                      onClick={() => deleteCompany(deleteConfirm.id)}
                      data-testid="delete-confirm-btn">
                      Delete Forever
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* TOP USERS TAB */}
        {activeTab === 'users' && topUsers && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00A6A0]" />Top Users by Shifts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topUsers.top_by_shifts?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No shift data yet</p>
                ) : (
                  <div className="space-y-2">
                    {topUsers.top_by_shifts?.map((entry, i) => (
                      <div key={`shift-${i}`} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'}`}>
                            {i < 3 ? <Crown className="w-4 h-4" /> : i + 1}
                          </div>
                          <div>
                            <p className="text-sm text-foreground">{entry.user_name}</p>
                            <p className="text-[10px] text-muted-foreground capitalize">{entry.role?.replace('_', ' ')} {entry.staff_number ? `- ${entry.staff_number}` : ''}</p>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-[#00A6A0]">{entry.shift_count} shifts</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#FF6F61]" />Top Users by Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topUsers.top_by_messages?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No message data yet</p>
                ) : (
                  <div className="space-y-2">
                    {topUsers.top_by_messages?.map((entry, i) => (
                      <div key={`msg-${i}`} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">{i + 1}</div>
                          <p className="text-sm text-foreground">{entry.user_name}</p>
                        </div>
                        <p className="text-sm font-bold text-[#FF6F61]">{entry.message_count} msgs</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {topUsers.user_growth?.length > 0 && (
              <Card className="glass-card border-border">
                <CardHeader>
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#A8E6CF]" />User Growth (Monthly)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2 h-32">
                    {topUsers.user_growth.map((g, i) => {
                      const maxCount = Math.max(...topUsers.user_growth.map(x => x.count), 1);
                      const height = (g.count / maxCount) * 100;
                      return (
                        <div key={`growth-${i}`} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] text-[#00A6A0] font-bold">{g.count}</span>
                          <div className="w-full bg-[#00A6A0]/20 rounded-t" style={{ height: `${Math.max(height, 4)}%` }}>
                            <div className="w-full h-full bg-[#00A6A0] rounded-t" />
                          </div>
                          <span className="text-[8px] text-muted-foreground">{g.month?.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* MOBILE API TAB */}
        {activeTab === 'mobile' && (
          <div className="space-y-6" data-testid="mobile-api-tab">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#00A6A0]" />Mobile API Endpoints
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">The backend is fully ready for native mobile app integration with Bearer token authentication.</p>
                {[
                  { method: 'POST', path: '/api/auth/token', desc: 'Get Bearer token (email + password)', status: 'active' },
                  { method: 'POST', path: '/api/mobile/sync', desc: 'Batch data sync (shifts, messages, journals, meds)', status: 'active' },
                  { method: 'GET', path: '/api/mobile/push-config', desc: 'Push notification configuration & registered devices', status: 'active' },
                  { method: 'PUT', path: '/api/mobile/push-preferences', desc: 'Update push event preferences', status: 'active' },
                  { method: 'POST', path: '/api/devices/register', desc: 'Register device (iOS/Android/web)', status: 'active' },
                  { method: 'GET', path: '/api/api-info', desc: 'API capabilities & version (public)', status: 'active' },
                ].map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${ep.method === 'GET' ? 'bg-emerald-500/10 text-emerald-400' : ep.method === 'POST' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {ep.method}
                    </span>
                    <div className="flex-1">
                      <p className="text-xs text-foreground font-mono">{ep.path}</p>
                      <p className="text-[10px] text-muted-foreground">{ep.desc}</p>
                    </div>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{ep.status}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF6F61]" />Authentication Flow
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { step: '1', text: 'POST /api/auth/token with email & password', detail: 'Returns access_token (Bearer)' },
                    { step: '2', text: 'Include token in Authorization header', detail: 'Authorization: Bearer <token>' },
                    { step: '3', text: 'Register device for push notifications', detail: 'POST /api/devices/register with device_token + platform' },
                    { step: '4', text: 'Batch sync data for offline use', detail: 'POST /api/mobile/sync with last_sync timestamp' },
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-3 p-3 bg-muted rounded-lg border border-border">
                      <div className="w-6 h-6 rounded-full bg-[#00A6A0]/20 flex items-center justify-center text-[10px] font-bold text-[#00A6A0] shrink-0">{s.step}</div>
                      <div>
                        <p className="text-xs text-foreground">{s.text}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{s.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#A8E6CF]" />Supported Push Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    'shift_assigned', 'shift_reminder', 'shift_swap_request',
                    'message_received', 'voice_message', 'emergency_alert',
                    'leave_approved', 'leave_rejected', 'payslip_ready',
                    'review_published', 'warning_issued', 'broadcast',
                    'medication_due', 'incident_update'
                  ].map(evt => (
                    <div key={evt} className="p-2 bg-muted rounded border border-border text-center">
                      <p className="text-[10px] text-muted-foreground capitalize">{evt.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AUDIT LOG TAB */}
        {activeTab === 'audit' && (
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00A6A0]" />Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">No audit events yet</p>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log, idx) => (
                    <div key={log.id || idx} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Activity className="w-4 h-4 text-[#00A6A0]" />
                        <div>
                          <p className="text-sm text-foreground">{log.action?.replace(/_/g, ' ')}</p>
                          <p className="text-[10px] text-muted-foreground">{log.performed_by_role} | {log.target_org_id ? `Org: ${log.target_org_id.slice(-6)}` : log.user_id?.slice(-6) || ''}</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* VERIFICATIONS TAB */}
        {activeTab === 'verifications' && verifications && (
          <VerificationPanel verifications={verifications} onRefresh={fetchAll} />
        )}

        {/* PROMOTIONS TAB */}
        {activeTab === 'promotions' && (
          <PromotionsPanel concessions={concessions} promoCodes={promoCodes} companies={companies} onRefresh={fetchAll} />
        )}

        {/* MARKETING TAB */}
        {activeTab === 'marketing' && (
          <div className="space-y-6" data-testid="marketing-tab">
            <Card className="glass-card border-[#FF6F61]/20">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                      <Film className="w-6 h-6 text-[#FF6F61]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground">
                        Marketing Assets
                      </h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Presentation deck, ad video clips, tutorial videos, and voiceover scripts
                      </p>
                    </div>
                  </div>
                  <Button className="bg-[#FF6F61] hover:bg-[#e55a4d] text-white"
                    onClick={() => navigate('/marketing-assets')}
                    data-testid="marketing-assets-btn">
                    Open Assets Page
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-5 h-5 text-[#FF6F61]" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">PowerPoint Deck</p>
                  <p className="text-xs text-muted-foreground mt-1">11 slides, ~4MB</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-[#00A6A0]/10 flex items-center justify-center mx-auto mb-3">
                    <Film className="w-5 h-5 text-[#00A6A0]" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Ad Video</p>
                  <p className="text-xs text-muted-foreground mt-1">5 clips + voiceover script</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-3">
                    <Film className="w-5 h-5 text-[#4A90E2]" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Tutorial Video</p>
                  <p className="text-xs text-muted-foreground mt-1">4 clips + voiceover script</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ====== ACCESS ANALYTICS TAB ====== */}
        {activeTab === 'access-analytics' && (
          <AccessAnalyticsTab API_URL={API_URL} />
        )}

        {/* ====== BILLING TAB ====== */}
        {activeTab === 'billing' && (
          <BillingTab API_URL={API_URL} />
        )}
      </div>
    </DashboardLayout>
  );
}

function VerificationPanel({ verifications, onRefresh }) {
  const [reviewingOrg, setReviewingOrg] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const handleReview = async () => {
    if (!reviewingOrg) return;
    if (reviewStatus === 'rejected' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/verification/review`, {
        organization_id: reviewingOrg.id,
        status: reviewStatus,
        rejection_reason: rejectionReason
      }, { withCredentials: true });
      toast.success(`Agency ${reviewStatus}`);
      setReviewingOrg(null);
      setRejectionReason('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Review failed'); }
    setSubmitting(false);
  };

  const analytics = verifications.analytics || {};
  const orgs = verifications.organizations || [];
  const filtered = filterStatus === 'all' ? orgs : orgs.filter(o => o.verification_status === filterStatus);

  const VSTATUS = {
    not_started: { label: 'Not Started', color: 'text-zinc-400', bg: 'bg-zinc-500/10', icon: ClipboardList },
    submitted: { label: 'Pending Review', color: 'text-amber-500', bg: 'bg-amber-500/10', icon: Clock },
    approved: { label: 'Approved', color: 'text-emerald-500', bg: 'bg-emerald-500/10', icon: CheckCircle2 },
    rejected: { label: 'Rejected', color: 'text-red-500', bg: 'bg-red-500/10', icon: XCircle },
  };

  return (
    <div className="space-y-6" data-testid="verification-panel">
      {/* Analytics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(VSTATUS).map(([status, config]) => {
          const Icon = config.icon;
          return (
            <Card
              key={status}
              className={`border cursor-pointer transition-all ${filterStatus === status ? 'ring-2 ring-[#00A6A0]/50' : 'border-border'}`}
              onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
              data-testid={`verification-filter-${status}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${config.bg}`}>
                  <Icon className={`w-4 h-4 ${config.color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                  <p className={`text-xl font-bold ${config.color}`}>{analytics[status] || 0}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Org List */}
      <Card className="glass-card border-border">
        <CardHeader>
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#00A6A0]" />
            Agency Verifications ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">No agencies found</p>
          ) : (
            <div className="space-y-2">
              {filtered.map(org => {
                const vs = VSTATUS[org.verification_status] || VSTATUS.not_started;
                const VIcon = vs.icon;
                const checklist = org.checklist || {};
                const completedItems = Object.values(checklist).filter(Boolean).length;
                return (
                  <div key={org.id} className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/50" data-testid={`verification-org-${org.id}`}>
                    <div className={`p-2 rounded-lg ${vs.bg}`}>
                      <VIcon className={`w-5 h-5 ${vs.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{org.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {org.agency_type || 'Agency'} · {org.total_users} user(s) · {completedItems}/6 checklist
                      </p>
                      {org.rejection_reason && (
                        <p className="text-xs text-red-400 mt-0.5">Reason: {org.rejection_reason}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${vs.bg} ${vs.color}`}>{vs.label}</span>
                    {org.verification_status === 'submitted' && (
                      <Button size="sm" className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={() => { setReviewingOrg(org); setReviewStatus('approved'); setRejectionReason(''); }} data-testid={`review-org-${org.id}`}>
                        Review
                      </Button>
                    )}
                    {(org.verification_status === 'rejected' || org.verification_status === 'not_started') && (
                      <Button size="sm" variant="outline" className="border-border text-foreground" onClick={() => { setReviewingOrg(org); setReviewStatus('approved'); setRejectionReason(''); }} data-testid={`review-org-${org.id}`}>
                        Review
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      {reviewingOrg && (
        <Dialog open={!!reviewingOrg} onOpenChange={() => setReviewingOrg(null)}>
          <DialogContent className="glass-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground">Review: {reviewingOrg.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground mb-2">Checklist Completion</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {['company_registration', 'insurance_certificate', 'dbs_policy', 'safeguarding_policy', 'cqc_registration', 'employer_liability'].map(key => (
                    <div key={key} className="flex items-center gap-2">
                      {(reviewingOrg.checklist || {})[key]
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        : <XCircle className="w-3.5 h-3.5 text-red-500" />
                      }
                      <span className="text-xs text-foreground">{key.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-foreground">Decision</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant={reviewStatus === 'approved' ? 'default' : 'outline'}
                    className={reviewStatus === 'approved' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'border-border text-foreground'}
                    onClick={() => setReviewStatus('approved')}
                    data-testid="review-approve-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    variant={reviewStatus === 'rejected' ? 'default' : 'outline'}
                    className={reviewStatus === 'rejected' ? 'bg-red-500 hover:bg-red-600 text-white' : 'border-border text-foreground'}
                    onClick={() => setReviewStatus('rejected')}
                    data-testid="review-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
              {reviewStatus === 'rejected' && (
                <div>
                  <Label className="text-foreground">Rejection Reason *</Label>
                  <textarea
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[80px] resize-none"
                    placeholder="Explain why this verification is being rejected..."
                    data-testid="review-rejection-reason"
                  />
                </div>
              )}
              <Button
                className={`w-full text-white ${reviewStatus === 'approved' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}`}
                onClick={handleReview}
                disabled={submitting}
                data-testid="submit-review-btn"
              >
                {submitting ? 'Submitting...' : `Confirm ${reviewStatus === 'approved' ? 'Approval' : 'Rejection'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default DeveloperDashboard;

// ═══════════════════════════════════════════════
//  PROMOTIONS PANEL (Concessions + Promo Codes)
// ═══════════════════════════════════════════════

function PromotionsPanel({ concessions, promoCodes, companies, onRefresh }) {
  const [subTab, setSubTab] = useState('concessions');
  const [showConcessionDialog, setShowConcessionDialog] = useState(false);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Concession form
  const [conOrgId, setConOrgId] = useState('');
  const [conType, setConType] = useState('percentage_discount');
  const [conValue, setConValue] = useState('');
  const [conReason, setConReason] = useState('');
  const [conExpiry, setConExpiry] = useState('');

  // Promo form
  const [proCode, setProCode] = useState('');
  const [proName, setProName] = useState('');
  const [proDesc, setProDesc] = useState('');
  const [proDiscountType, setProDiscountType] = useState('percentage');
  const [proDiscountValue, setProDiscountValue] = useState('');
  const [proMaxUses, setProMaxUses] = useState('');
  const [proValidFrom, setProValidFrom] = useState('');
  const [proValidUntil, setProValidUntil] = useState('');
  const [proTargetPlans, setProTargetPlans] = useState([]);

  const CONCESSION_TYPES = {
    percentage_discount: { label: 'Percentage Discount', unit: '%', icon: Percent },
    free_months: { label: 'Free Months', unit: 'months', icon: Gift },
    custom_price: { label: 'Custom Price', unit: '$/mo', icon: DollarSign },
    plan_upgrade: { label: 'Plan Upgrade', unit: 'tier', icon: ArrowUpRight },
  };

  const PLAN_OPTIONS = [
    { id: 'individual', name: 'Individual ($24.99)' },
    { id: 'tier_1_10', name: 'Starter ($120.99)' },
    { id: 'tier_11_50', name: 'Growth ($169.99)' },
    { id: 'tier_51_100', name: 'Professional ($200)' },
    { id: 'tier_100_plus', name: 'Enterprise ($239)' },
  ];

  const handleCreateConcession = async () => {
    if (!conOrgId || !conValue) return toast.error('Organization and value required');
    setSubmitting(true);
    try {
      let parsedValue = conType === 'plan_upgrade' ? conValue : parseFloat(conValue);
      await axios.post(`${API_URL}/api/developer/concessions`, {
        organization_id: conOrgId,
        type: conType,
        value: parsedValue,
        reason: conReason,
        expires_at: conExpiry || null,
      }, { withCredentials: true });
      toast.success('Concession created');
      setShowConcessionDialog(false);
      resetConcessionForm();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const handleRevokeConcession = async (id) => {
    if (!window.confirm('Revoke this concession?')) return;
    try {
      await axios.delete(`${API_URL}/api/developer/concessions/${id}`, { withCredentials: true });
      toast.success('Concession revoked');
      onRefresh();
    } catch (err) { toast.error('Failed to revoke'); }
  };

  const handleCreatePromo = async () => {
    if (!proDiscountValue) return toast.error('Discount value required');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/developer/promo-codes`, {
        code: proCode || undefined,
        name: proName,
        description: proDesc,
        discount_type: proDiscountType,
        discount_value: parseFloat(proDiscountValue),
        max_uses: proMaxUses ? parseInt(proMaxUses) : 0,
        valid_from: proValidFrom || undefined,
        valid_until: proValidUntil || undefined,
        target_plans: proTargetPlans,
      }, { withCredentials: true });
      toast.success('Promo code created');
      setShowPromoDialog(false);
      resetPromoForm();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const handleDeactivatePromo = async (id) => {
    if (!window.confirm('Deactivate this promo code?')) return;
    try {
      await axios.delete(`${API_URL}/api/developer/promo-codes/${id}`, { withCredentials: true });
      toast.success('Promo code deactivated');
      onRefresh();
    } catch (err) { toast.error('Failed'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const resetConcessionForm = () => { setConOrgId(''); setConType('percentage_discount'); setConValue(''); setConReason(''); setConExpiry(''); };
  const resetPromoForm = () => { setProCode(''); setProName(''); setProDesc(''); setProDiscountType('percentage'); setProDiscountValue(''); setProMaxUses(''); setProValidFrom(''); setProValidUntil(''); setProTargetPlans([]); };

  const togglePlan = (planId) => {
    setProTargetPlans(prev => prev.includes(planId) ? prev.filter(p => p !== planId) : [...prev, planId]);
  };

  return (
    <div className="space-y-6" data-testid="promotions-panel">
      {/* Sub-tabs */}
      <div className="flex gap-2">
        <Button size="sm" variant={subTab === 'concessions' ? 'default' : 'outline'}
          className={subTab === 'concessions' ? 'bg-[#00A6A0] text-white' : 'border-border text-muted-foreground'}
          onClick={() => setSubTab('concessions')} data-testid="concessions-tab">
          <Gift className="w-4 h-4 mr-1.5" />Concessions ({concessions.filter(c => c.status === 'active').length})
        </Button>
        <Button size="sm" variant={subTab === 'promo' ? 'default' : 'outline'}
          className={subTab === 'promo' ? 'bg-[#FF6F61] text-white' : 'border-border text-muted-foreground'}
          onClick={() => setSubTab('promo')} data-testid="promo-codes-tab">
          <Tag className="w-4 h-4 mr-1.5" />Promo Codes ({promoCodes.filter(p => p.status === 'active').length})
        </Button>
      </div>

      {/* ══════ CONCESSIONS ══════ */}
      {subTab === 'concessions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading font-bold text-foreground">Agency Concessions</h3>
            <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={() => setShowConcessionDialog(true)} data-testid="new-concession-btn">
              <Gift className="w-4 h-4 mr-1.5" />New Concession
            </Button>
          </div>

          {concessions.length === 0 ? (
            <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No concessions yet</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {concessions.map(con => {
                const typeInfo = CONCESSION_TYPES[con.type] || {};
                const TypeIcon = typeInfo.icon || Gift;
                const isActive = con.status === 'active';
                const isExpired = con.expires_at && new Date(con.expires_at) < new Date();
                return (
                  <Card key={con.id} className={`border ${isActive && !isExpired ? 'border-[#00A6A0]/30' : 'border-border opacity-60'}`} data-testid={`concession-${con.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-[#00A6A0]/10' : 'bg-muted'}`}>
                        <TypeIcon className={`w-5 h-5 ${isActive ? 'text-[#00A6A0]' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{con.organization_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {typeInfo.label}: <span className="font-semibold text-[#00A6A0]">{con.value}{typeInfo.unit === '%' ? '%' : ` ${typeInfo.unit}`}</span>
                          {con.reason && <> — {con.reason}</>}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Created {new Date(con.created_at).toLocaleDateString()}
                          {con.expires_at && <> · Expires {new Date(con.expires_at).toLocaleDateString()}</>}
                          {isExpired && <span className="text-red-400 ml-1">(Expired)</span>}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive && !isExpired ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                        {isExpired ? 'Expired' : con.status}
                      </span>
                      {isActive && !isExpired && (
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleRevokeConcession(con.id)} data-testid={`revoke-${con.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Create Concession Dialog */}
          <Dialog open={showConcessionDialog} onOpenChange={setShowConcessionDialog}>
            <DialogContent className="glass-card border-border sm:max-w-md">
              <DialogHeader><DialogTitle className="text-foreground">New Agency Concession</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-foreground">Agency</Label>
                  <select value={conOrgId} onChange={e => setConOrgId(e.target.value)} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="concession-org-select">
                    <option value="">Select agency...</option>
                    {(companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-foreground">Concession Type</Label>
                  <select value={conType} onChange={e => setConType(e.target.value)} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="concession-type-select">
                    {Object.entries(CONCESSION_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-foreground">Value {CONCESSION_TYPES[conType]?.unit && `(${CONCESSION_TYPES[conType].unit})`}</Label>
                  {conType === 'plan_upgrade' ? (
                    <select value={conValue} onChange={e => setConValue(e.target.value)} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="concession-value">
                      <option value="">Select tier...</option>
                      {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : (
                    <Input type="number" value={conValue} onChange={e => setConValue(e.target.value)} placeholder={conType === 'percentage_discount' ? 'e.g., 20' : conType === 'free_months' ? 'e.g., 3' : 'e.g., 99.00'} className="mt-1 bg-muted border-border text-foreground" data-testid="concession-value" />
                  )}
                </div>
                <div>
                  <Label className="text-foreground">Reason</Label>
                  <Input value={conReason} onChange={e => setConReason(e.target.value)} placeholder="e.g., Early adopter deal, Partner agency" className="mt-1 bg-muted border-border text-foreground" data-testid="concession-reason" />
                </div>
                <div>
                  <Label className="text-foreground">Expiry Date (optional)</Label>
                  <Input type="date" value={conExpiry} onChange={e => setConExpiry(e.target.value)} className="mt-1 bg-muted border-border text-foreground" data-testid="concession-expiry" />
                </div>
                <Button className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={handleCreateConcession} disabled={submitting} data-testid="submit-concession-btn">
                  {submitting ? 'Creating...' : 'Create Concession'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ══════ PROMO CODES ══════ */}
      {subTab === 'promo' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading font-bold text-foreground">Promo Codes</h3>
            <Button className="bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white" onClick={() => setShowPromoDialog(true)} data-testid="new-promo-btn">
              <Tag className="w-4 h-4 mr-1.5" />New Promo Code
            </Button>
          </div>

          {promoCodes.length === 0 ? (
            <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Tag className="w-12 h-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No promo codes yet</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {promoCodes.map(promo => {
                const isActive = promo.status === 'active';
                const isExpired = promo.valid_until && new Date(promo.valid_until) < new Date();
                const usageText = promo.max_uses > 0 ? `${promo.current_uses}/${promo.max_uses} used` : `${promo.current_uses} used`;
                return (
                  <Card key={promo.id} className={`border ${isActive && !isExpired ? 'border-[#FF6F61]/30' : 'border-border opacity-60'}`} data-testid={`promo-${promo.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-[#FF6F61]/10' : 'bg-muted'}`}>
                        <Tag className={`w-5 h-5 ${isActive ? 'text-[#FF6F61]' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-0.5 rounded bg-muted text-sm font-mono font-bold text-foreground">{promo.code}</code>
                          <button onClick={() => copyCode(promo.code)} className="text-muted-foreground hover:text-foreground" data-testid={`copy-${promo.code}`}><Copy className="w-3.5 h-3.5" /></button>
                          {promo.name && promo.name !== promo.code && <span className="text-xs text-muted-foreground">— {promo.name}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {promo.discount_type === 'percentage' && `${promo.discount_value}% off`}
                          {promo.discount_type === 'free_months' && `${promo.discount_value} free months`}
                          {promo.discount_type === 'fixed_amount' && `$${promo.discount_value} off`}
                          {' · '}{usageText}
                          {promo.target_plans?.length > 0 && <> · Plans: {promo.target_plans.join(', ')}</>}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {promo.valid_from && <>From {new Date(promo.valid_from).toLocaleDateString()}</>}
                          {promo.valid_until && <> to {new Date(promo.valid_until).toLocaleDateString()}</>}
                          {isExpired && <span className="text-red-400 ml-1">(Expired)</span>}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive && !isExpired ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                        {isExpired ? 'Expired' : promo.status}
                      </span>
                      {isActive && (
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeactivatePromo(promo.id)} data-testid={`deactivate-${promo.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Create Promo Dialog */}
          <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
            <DialogContent className="glass-card border-border sm:max-w-lg">
              <DialogHeader><DialogTitle className="text-foreground">New Promo Code</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-foreground">Code (auto-generated if empty)</Label>
                    <Input value={proCode} onChange={e => setProCode(e.target.value.toUpperCase())} placeholder="e.g., XMAS2026" className="mt-1 bg-muted border-border text-foreground font-mono" data-testid="promo-code-input" />
                  </div>
                  <div>
                    <Label className="text-foreground">Campaign Name</Label>
                    <Input value={proName} onChange={e => setProName(e.target.value)} placeholder="e.g., Christmas Sale" className="mt-1 bg-muted border-border text-foreground" data-testid="promo-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Description</Label>
                  <Input value={proDesc} onChange={e => setProDesc(e.target.value)} placeholder="Internal note about this campaign" className="mt-1 bg-muted border-border text-foreground" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-foreground">Discount Type</Label>
                    <select value={proDiscountType} onChange={e => setProDiscountType(e.target.value)} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="promo-discount-type">
                      <option value="percentage">Percentage Off</option>
                      <option value="free_months">Free Months</option>
                      <option value="fixed_amount">Fixed $ Off</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-foreground">Value</Label>
                    <Input type="number" value={proDiscountValue} onChange={e => setProDiscountValue(e.target.value)} placeholder={proDiscountType === 'percentage' ? '20' : proDiscountType === 'free_months' ? '3' : '50'} className="mt-1 bg-muted border-border text-foreground" data-testid="promo-discount-value" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-foreground">Valid From</Label>
                    <Input type="date" value={proValidFrom} onChange={e => setProValidFrom(e.target.value)} className="mt-1 bg-muted border-border text-foreground" data-testid="promo-valid-from" />
                  </div>
                  <div>
                    <Label className="text-foreground">Valid Until</Label>
                    <Input type="date" value={proValidUntil} onChange={e => setProValidUntil(e.target.value)} className="mt-1 bg-muted border-border text-foreground" data-testid="promo-valid-until" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Max Uses (0 = unlimited)</Label>
                  <Input type="number" value={proMaxUses} onChange={e => setProMaxUses(e.target.value)} placeholder="e.g., 100" className="mt-1 bg-muted border-border text-foreground" data-testid="promo-max-uses" />
                </div>
                <div>
                  <Label className="text-foreground">Target Plans (leave empty for all plans)</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {PLAN_OPTIONS.map(p => (
                      <button key={p.id} onClick={() => togglePlan(p.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${proTargetPlans.includes(p.id) ? 'bg-[#FF6F61] border-[#FF6F61] text-white' : 'bg-muted border-border text-muted-foreground hover:border-[#FF6F61]/50'}`}
                        data-testid={`target-plan-${p.id}`}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
                <Button className="w-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white" onClick={handleCreatePromo} disabled={submitting} data-testid="submit-promo-btn">
                  {submitting ? 'Creating...' : 'Create Promo Code'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}


function AccessAnalyticsTab({ API_URL }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => { fetchData(); }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API_URL}/api/access/analytics?days=${days}`, { withCredentials: true });
      setData(res);
    } catch { toast.error('Failed to load analytics'); }
    setLoading(false);
  };

  if (loading) return <div className="text-center py-8"><p className="text-muted-foreground">Loading analytics...</p></div>;
  if (!data) return null;

  const maxDaily = Math.max(...data.daily_frequency.map(d => d.count), 1);
  const maxHour = Math.max(...data.peak_hours.map(h => h.count), 1);

  return (
    <div className="space-y-6" data-testid="access-analytics-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{data.total_events} access events in last {data.period_days} days</p>
        <div className="flex gap-1">
          {[7, 14, 30, 90].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} className={`text-[10px] h-6 px-2 ${days === d ? 'bg-[#FF6F61] text-white' : ''}`}
              onClick={() => setDays(d)}>{d}d</Button>
          ))}
        </div>
      </div>

      <Card className="glass-card border-border">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Daily Login Frequency</h3>
          <div className="flex items-end gap-1 h-32" data-testid="daily-chart">
            {data.daily_frequency.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
                <div className="w-full bg-[#FF6F61]/70 rounded-t" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }} />
                {i % Math.max(1, Math.floor(data.daily_frequency.length / 7)) === 0 && (
                  <span className="text-[8px] text-muted-foreground truncate">{d.date.slice(5)}</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Peak Hours (UTC)</h3>
            <div className="flex items-end gap-0.5 h-24" data-testid="hours-chart">
              {data.peak_hours.map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full bg-[#00A6A0]/60 rounded-t" style={{ height: `${(h.count / maxHour) * 100}%`, minHeight: h.count > 0 ? 2 : 0 }} />
                  {i % 4 === 0 && <span className="text-[8px] text-muted-foreground">{i}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Device Breakdown</h3>
            <div className="space-y-2" data-testid="device-chart">
              {data.device_breakdown.map(d => (
                <div key={d.type} className="flex items-center gap-2">
                  <span className="text-xs text-foreground w-20">{d.type}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-[#FF6F61]/50 rounded-full" style={{ width: `${(d.count / data.total_events) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{d.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Browser Breakdown</h3>
            <div className="space-y-2" data-testid="browser-chart">
              {data.browser_breakdown.map(b => (
                <div key={b.browser} className="flex items-center gap-2">
                  <span className="text-xs text-foreground w-20 truncate">{b.browser}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-[#00A6A0]/50 rounded-full" style={{ width: `${(b.count / data.total_events) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{b.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">OS Breakdown</h3>
            <div className="space-y-2" data-testid="os-chart">
              {data.os_breakdown.map(o => (
                <div key={o.os} className="flex items-center gap-2">
                  <span className="text-xs text-foreground w-20 truncate">{o.os}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-purple-500/50 rounded-full" style={{ width: `${(o.count / data.total_events) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{o.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Top IP Addresses</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="ip-list">
              {data.top_ips.map(ip => (
                <div key={ip.ip} className="flex items-center justify-between p-1.5 bg-muted/30 rounded text-xs">
                  <span className="text-foreground font-mono">{ip.ip || 'unknown'}</span>
                  <span className="text-muted-foreground">{ip.count} hits</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Roles Accessing Dashboard</h3>
            <div className="space-y-2" data-testid="role-chart">
              {data.role_breakdown.map(r => (
                <div key={r.role} className="flex items-center gap-2">
                  <span className="text-xs text-foreground w-24">{r.role}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-amber-500/50 rounded-full" style={{ width: `${(r.count / data.total_events) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{r.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function BillingTab({ API_URL }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/billing/invoices-all`, { withCredentials: true });
      setInvoices(data.invoices || []);
    } catch { /* */ }
    setLoading(false);
  };

  const generateBilling = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/billing/generate-monthly`, {}, { withCredentials: true });
      toast.success('Monthly billing generated');
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setGenerating(false);
  };

  return (
    <div className="space-y-4" data-testid="billing-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{invoices.length} subscription invoices</p>
        <Button size="sm" className="bg-[#FF6F61] text-white" onClick={generateBilling} disabled={generating} data-testid="generate-billing-btn">
          <DollarSign className="w-4 h-4 mr-1" />{generating ? 'Generating...' : 'Generate Monthly Billing'}
        </Button>
      </div>

      {loading ? <p className="text-muted-foreground text-center py-8">Loading...</p> : invoices.length === 0 ? (
        <div className="text-center py-8"><DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No invoices yet</p></div>
      ) : (
        <div className="space-y-2">
          {invoices.map(inv => (
            <Card key={inv.id} className="glass-card border-border" data-testid={`invoice-${inv.invoice_number}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{inv.invoice_number}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${inv.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{inv.status}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{inv.client_name} &middot; {inv.billing_period}</p>
                    <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                      {inv.usage && <span>{inv.usage.total_users} users</span>}
                      {inv.usage && <span>{inv.usage.active_admins} admins</span>}
                      {inv.discount_pct > 0 && <span className="text-emerald-400">{inv.discount_pct}% off</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#FF6F61]">£{inv.total}</p>
                    <p className="text-[10px] text-muted-foreground">Due: {inv.due_date}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
